import type { GetUserSessionsResponse, LogoutSessionResponse, UserSessionInfo } from '@nauth-toolkit/client';
import { authClient } from '../../lib/auth';

/**
 * Account → Security. All of it is nauth identity (password, sessions, MFA), so
 * all of it goes through the SDK client. Name and email come from Rundown's
 * `/me` via `useAuth().user`, not nauth's profile.
 */

export interface ChangePasswordInput {
  readonly oldPassword: string;
  readonly newPassword: string;
}

/**
 * nauth revokes every session on a successful change — this one included — so
 * the caller must treat the current session as dead the instant this resolves.
 */
export function changePassword(input: ChangePasswordInput): Promise<void> {
  return authClient.changePassword(input.oldPassword, input.newPassword);
}

export type AccountSession = UserSessionInfo;

export function fetchSessions(): Promise<GetUserSessionsResponse> {
  return authClient.listSessions();
}

/** `wasCurrentSession` tells the caller it just revoked its own session. */
export function revokeSession(sessionId: string): Promise<LogoutSessionResponse> {
  return authClient.revokeSession(sessionId);
}

/**
 * The shape the API actually returns (`GetMFAStatusResponseDTO` in nauth core
 * 0.8.1). The client SDK's `MFAStatus` type names the device list `methods`,
 * but the server sends `configuredMethods`.
 */
export interface MfaStatus {
  readonly enabled: boolean;
  readonly required: boolean;
  readonly configuredMethods: readonly string[];
  readonly availableMethods: readonly string[];
  readonly hasBackupCodes: boolean;
  readonly preferredMethod?: string;
  readonly mfaExempt: boolean;
}

export async function fetchMfaStatus(): Promise<MfaStatus> {
  const status: unknown = await authClient.getMfaStatus();
  return status as MfaStatus;
}

/**
 * Sign-in methods — nauth's `social` route group.
 *
 * Rundown links Google to an account BY VERIFIED EMAIL: `auth.config.ts` sets
 * `autoLink: true` with `allowSignup: false`, so signing in through Google
 * attaches that Google identity to the Rundown account with the same address
 * and refuses anything else. That is what makes "Link Google" a plain redirect
 * below rather than a call to nauth's `POST /auth/social/link`: in the
 * redirect-first web flow the backend's own callback consumes the OAuth `code`,
 * so the browser never holds the `code`/`state` pair that route requires. It is
 * for native apps, which is why it is not used here.
 */

/** One row of `GET /auth/social/linked`. Dates are ISO-8601. */
export interface LinkedAccount {
  readonly provider: string;
  readonly providerEmail: string | null;
  readonly linkedAt: string;
  readonly lastUsedAt: string | null;
}

/**
 * The SDK types this response as `{ providers: SocialProvider[] }`, but the
 * running API sends `GetLinkedAccountsResponseDTO` — `{ accounts: [...] }` with
 * the provider email and timestamps on each. Same mismatch as MFA status (see
 * `MfaStatus` above), so the body is narrowed here rather than trusted.
 */
export async function fetchLinkedAccounts(): Promise<readonly LinkedAccount[]> {
  const body: unknown = await authClient.getLinkedAccounts();
  const accounts = (body as { accounts?: readonly Record<string, unknown>[] }).accounts ?? [];
  return accounts.map((account) => ({
    provider: String(account.provider),
    providerEmail: typeof account.providerEmail === 'string' ? account.providerEmail : null,
    linkedAt: new Date(account.linkedAt as string).toISOString(),
    lastUsedAt: typeof account.lastUsedAt === 'string' ? new Date(account.lastUsedAt).toISOString() : null,
  }));
}

export async function unlinkSocialAccount(provider: string): Promise<void> {
  await authClient.unlinkSocialAccount(provider);
}

/**
 * True for an account that has no password at all — Google-only. nauth answers
 * it from `passwordHash`, so it doubles as "does this person have a second way
 * in", which is what Sign-in methods needs before it will offer to unlink
 * Google.
 */
export async function fetchCanSetPassword(): Promise<boolean> {
  const result = await authClient.canSetPassword();
  return result.canSetPassword;
}

/**
 * First password for a Google-only account. Like `changePassword`, this revokes
 * every session including the caller's — nauth routes it through the same
 * `updateUserPassword` helper — so the caller must end the local session after
 * it resolves.
 */
export async function setFirstPassword(newPassword: string): Promise<void> {
  await authClient.setPasswordForSocialUser(newPassword);
}

/** Where the OAuth round trip comes back to, and how this screen recognises it. */
export const LINK_RETURN_PATH = '/account/security';
export const LINK_APP_STATE = `${LINK_RETURN_PATH}?linked=google`;
/**
 * Who asked for the link, remembered across the redirect.
 *
 * Picking a *different* Google account at the chooser does not link it — it
 * signs you in as whoever owns that address instead. Stashing the address that
 * started the flow is what lets the screen say that plainly afterwards rather
 * than leaving someone looking at another person's account.
 */
export const LINK_INTENT_KEY = 'rundown.link-google';

export function startGoogleLink(email: string): Promise<void> {
  try {
    sessionStorage.setItem(LINK_INTENT_KEY, email);
  } catch {
    // Private browsing, or storage disabled. The link still works; only the
    // "you signed in as someone else" check below is lost.
  }
  return authClient.loginWithSocial('google', {
    returnTo: '/auth/callback',
    appState: LINK_APP_STATE,
    // Only the Google account with this same address can link, so point the
    // chooser at it. Merged over the provider's configured params rather than
    // replacing them, so `prompt: select_account` from auth.config.ts still
    // applies and the hint stays a suggestion.
    oauthParams: { login_hint: email },
  });
}
